import React, { useState, useEffect } from 'react';
import { 
  siftApiClient, 
  type AdminUser, 
  type CreateUserRequest, 
  type AdminPermissions,
  type UserSession,
} from '../../api/siftApiClient';
import { FEATURE_DEFS, DEFAULT_FEATURE_KEYS, type FeatureFlag } from '../../featureFlags';
import { BrokerSettingsSection } from './BrokerSettingsSection';
import { CacheRefreshSection } from './CacheRefreshSection';
import './AdminPanel.css';

interface AdminPanelProps {
  onClose: () => void;
  impersonatedRole: string | null;
  onImpersonate: (role: string | null) => void;
  user: UserSession | null;
}

const AdminPanel: React.FC<AdminPanelProps> = ({ onClose, impersonatedRole, onImpersonate, user }) => {
  // Managers (10/13-char territory roles) + admins manage users; broker-only
  // users open the panel solely for the Broker Settings section.
  const canManageUsers = !!user && (
    user.is_admin ||
    user.role.split(',').map((r) => r.trim()).some((r) => r.length === 10 || r.length === 13)
  );
  // Broker settings are GLOBAL (fit weights, urgency thresholds, price bands,
  // buyer exclusions) and saving them kicks a buyer-match recompute, so the tab
  // mirrors the backend gate on PUT /broker-view/settings (_require_broker_admin):
  // admin or the `broker_admin` feature flag. Rank-and-file brokers hold
  // `broker_view` only and never see the tab.
  const canEditBrokerSettings = !!user && (
    user.is_admin ||
    (user.feature_flags ?? []).includes('broker_admin')
  );
  // Admin panel tabs: user management vs. Broker View settings vs. data refresh.
  // Broker-only users land straight on the Broker settings tab (they have no
  // user-management access); the Data Refresh tab is admin-only.
  const [activeTab, setActiveTab] = useState<'users' | 'broker' | 'data'>(canManageUsers ? 'users' : 'broker');
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newUser, setNewUser] = useState<CreateUserRequest>({
    email: '',
    role: '',
    display_name: '',
    active: true,
    feature_flags: [...DEFAULT_FEATURE_KEYS],
  });
  
  // Permissions state
  const [permissions, setPermissions] = useState<AdminPermissions | null>(null);
  
  // Role editing state - track which user is being edited and the pending value
  const [editingRole, setEditingRole] = useState<{ email: string; value: string } | null>(null);
  
  // Managed By editing state - track which user is being edited and the pending value
  const [editingManagedBy, setEditingManagedBy] = useState<{ email: string; value: string } | null>(null);
  
  // User search state
  const [userSearch, setUserSearch] = useState('');
  
  // Impersonation state
  const [impersonateInput, setImpersonateInput] = useState(impersonatedRole || '');

  const loadUsers = async () => {
    // Broker-only users can't call the admin user APIs (they'd 403); they open
    // the panel solely for the Broker Settings section.
    if (!canManageUsers) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const [usersResponse, permissionsResponse] = await Promise.all([
        siftApiClient.listUsers(),
        siftApiClient.getAdminPermissions()
      ]);
      setUsers(usersResponse.users);
      setPermissions(permissionsResponse);
      setError(null);
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { detail?: string } } };
      setError(axiosError?.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await siftApiClient.createUser(newUser);
      setShowCreateForm(false);
      setNewUser({ email: '', role: '', display_name: '', active: true, feature_flags: [...DEFAULT_FEATURE_KEYS] });
      loadUsers();
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { detail?: string } } };
      setError(axiosError?.response?.data?.detail || 'Failed to create user');
    }
  };

  // Toggle a feature flag for an existing user (immediate save).
  const handleToggleFeature = async (user: AdminUser, feature: FeatureFlag) => {
    const current = (user.feature_flags && user.feature_flags.length > 0)
      ? user.feature_flags
      : [...DEFAULT_FEATURE_KEYS];
    const next = current.includes(feature)
      ? current.filter((f) => f !== feature)
      : [...current, feature];
    try {
      await siftApiClient.updateUser(user.email, { feature_flags: next });
      loadUsers();
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { detail?: string } } };
      setError(axiosError?.response?.data?.detail || 'Failed to update features');
    }
  };

  // Toggle a feature flag in the create-user form (local state only).
  const toggleNewUserFeature = (feature: FeatureFlag) => {
    setNewUser((prev) => {
      const current = prev.feature_flags ?? [...DEFAULT_FEATURE_KEYS];
      const next = current.includes(feature)
        ? current.filter((f) => f !== feature)
        : [...current, feature];
      return { ...prev, feature_flags: next };
    });
  };

  const handleToggleStatus = async (user: AdminUser) => {
    try {
      await siftApiClient.updateUser(user.email, { active: !user.active });
      loadUsers();
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { detail?: string } } };
      setError(axiosError?.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleRoleChange = async (email: string, newRole: string) => {
    try {
      await siftApiClient.updateUser(email, { role: newRole });
      setEditingRole(null);
      loadUsers();
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { detail?: string } } };
      setError(axiosError?.response?.data?.detail || 'Failed to update role');
    }
  };

  const startEditingRole = (email: string, currentRole: string) => {
    setEditingRole({ email, value: currentRole });
  };

  const cancelEditingRole = () => {
    setEditingRole(null);
  };

  const submitRoleChange = () => {
    if (editingRole) {
      handleRoleChange(editingRole.email, editingRole.value);
    }
  };

  const handleManagedByChange = async (email: string, newManagedBy: string) => {
    try {
      await siftApiClient.updateUser(email, { managed_by: newManagedBy });
      setEditingManagedBy(null);
      loadUsers();
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { detail?: string } } };
      setError(axiosError?.response?.data?.detail || 'Failed to update managed by');
    }
  };

  const startEditingManagedBy = (email: string, currentManagedBy: string) => {
    setEditingManagedBy({ email, value: currentManagedBy || '' });
  };

  const cancelEditingManagedBy = () => {
    setEditingManagedBy(null);
  };

  const submitManagedByChange = () => {
    if (editingManagedBy) {
      handleManagedByChange(editingManagedBy.email, editingManagedBy.value);
    }
  };

  const handleDeleteUser = async (email: string) => {
    if (!confirm(`Delete user ${email}?`)) return;
    try {
      await siftApiClient.deleteUser(email);
      loadUsers();
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { detail?: string } } };
      setError(axiosError?.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleResetPassword = async (email: string) => {
    if (!confirm(`Reset password for ${email}? They will need to set up a new password on their next login.`)) return;
    try {
      await siftApiClient.resetUserPassword(email);
      setError(null);
      alert(`Password reset for ${email}. They will be prompted to set up a new password on their next login.`);
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { detail?: string } } };
      setError(axiosError?.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleExportCSV = async () => {
    try {
      await siftApiClient.exportUsersCSV();
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { detail?: string } } };
      setError(axiosError?.response?.data?.detail || 'Export failed');
    }
  };

  return (
    <div className="admin-panel-overlay">
      <div className="admin-panel glass-card">
        <div className="admin-header">
          <h2 className="glass-text-primary">⚙️ Admin Panel</h2>
          <button className="close-button glass-button" onClick={onClose}>✕</button>
        </div>

        <div className="admin-tabs" role="tablist">
          {canManageUsers && (
            <button
              role="tab"
              aria-selected={activeTab === 'users'}
              className={`admin-tab ${activeTab === 'users' ? 'is-active' : ''}`}
              onClick={() => setActiveTab('users')}
            >
              👥 User Management
            </button>
          )}
          {canEditBrokerSettings && (
            <button
              role="tab"
              aria-selected={activeTab === 'broker'}
              className={`admin-tab ${activeTab === 'broker' ? 'is-active' : ''}`}
              onClick={() => setActiveTab('broker')}
            >
              🛠️ Broker View Settings
            </button>
          )}
          {user?.is_admin && (
            <button
              role="tab"
              aria-selected={activeTab === 'data'}
              className={`admin-tab ${activeTab === 'data' ? 'is-active' : ''}`}
              onClick={() => setActiveTab('data')}
            >
              🗄️ Data Refresh
            </button>
          )}
        </div>

        <div className="admin-panel-body">
        {/* Role Impersonation Section - Admin Only */}
        {activeTab === 'users' && permissions?.is_admin && (
          <div className="impersonation-section glass-card-subtle">
            <h3 className="glass-text-primary">Role Impersonation</h3>
            <p className="glass-text-secondary impersonation-description">
              Enter a role to view the app as that user would see it. Use territory codes: 10 chars (Manager), 13 chars (Market Center), or 16-17 chars (RDS Territory).
            </p>
            <div className="impersonation-controls">
              <input
                type="text"
                className="glass-input impersonation-input"
                placeholder="Enter role (e.g., MANRDSXX02)"
                value={impersonateInput}
                onChange={(e) => setImpersonateInput(e.target.value)}
              />
              <div className="impersonation-buttons">
                <button 
                  className="glass-button impersonate-button"
                  onClick={() => onImpersonate(impersonateInput || null)}
                  disabled={!impersonateInput}
                >
                  {impersonatedRole ? 'Update' : 'Impersonate'}
                </button>
                {impersonatedRole && (
                  <button 
                    className="glass-button clear-button"
                    onClick={() => {
                      onImpersonate(null);
                      setImpersonateInput('');
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
            {impersonatedRole && (
              <div className="impersonation-active">
                ✓ Currently viewing as: <strong>{impersonatedRole}</strong>
              </div>
            )}
          </div>
        )}

        {/* User Management Section (managers/admins only) */}
        {activeTab === 'users' && canManageUsers && (
        <div className="user-management-section">
          <h3 className="glass-text-primary section-title">👥 User Management</h3>

        {error && (
          <div className="admin-error">
            {error}
            <button onClick={() => setError(null)}>✕</button>
          </div>
        )}

        <div className="admin-actions">
          {permissions?.can_create_users && (
            <button 
              className="create-user-button glass-button"
              onClick={() => setShowCreateForm(!showCreateForm)}
            >
              {showCreateForm ? 'Cancel' : '+ Add User'}
            </button>
          )}
          
          {permissions?.can_bulk_export && (
            <div className="bulk-import-section">
              <button 
                className="template-button glass-button"
                onClick={handleExportCSV}
              >
                📥 Export CSV
              </button>
            </div>
          )}
        </div>

        {showCreateForm && permissions?.can_create_users && (
          <form className="create-user-form glass-card-subtle" onSubmit={handleCreateUser}>
            <div className="form-row">
              <input
                type="email"
                placeholder="Email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                className="glass-input"
                required
              />
              <input
                type="text"
                placeholder="Display Name"
                value={newUser.display_name}
                onChange={(e) => setNewUser({ ...newUser, display_name: e.target.value })}
                className="glass-input"
              />
            </div>
            <div className="form-row">
              <input
                type="text"
                placeholder="Role (admin, territory code, or e.g. broker)"
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                className="glass-input"
                title="Enter 'admin', a territory code (10, 13, 16, or 17 characters), or a freeform flag-only role such as 'broker' (no territory access — controlled by feature flags)"
              />
              <input
                type="text"
                placeholder="Managed By (optional)"
                value={newUser.managed_by || ''}
                onChange={(e) => setNewUser({ ...newUser, managed_by: e.target.value })}
                className="glass-input"
                title="Name of the manager who oversees this user"
              />
            </div>
            <div className="form-row feature-select-row">
              <span className="feature-select-label glass-text-secondary">Features:</span>
              <div className="feature-flags-cell">
                {FEATURE_DEFS.map((f) => {
                  const active = (newUser.feature_flags ?? DEFAULT_FEATURE_KEYS).includes(f.key);
                  return (
                    <button
                      key={f.key}
                      type="button"
                      className={`feature-chip ${active ? 'on' : 'off'}`}
                      onClick={() => toggleNewUserFeature(f.key)}
                      title={f.description}
                    >
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <button type="submit" className="submit-button glass-button">Create User</button>
          </form>
        )}

        {/* User Search */}
        <div className="user-search-container">
          <div className="user-search-wrapper">
            <input
              type="text"
              className="glass-input user-search-input"
              placeholder="🔍 Search users by name, email, or role..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
            />
            {userSearch && (
              <button 
                className="clear-search-button"
                onClick={() => setUserSearch('')}
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>
          <span className="user-count glass-text-muted">
            {users.filter(u => {
              const search = userSearch.toLowerCase();
              return !search || 
                u.email.toLowerCase().includes(search) ||
                (u.display_name?.toLowerCase().includes(search)) ||
                u.role.toLowerCase().includes(search);
            }).length} of {users.length} users
          </span>
        </div>

        {loading ? (
          <div className="admin-loading glass-text-secondary">Loading users...</div>
        ) : (
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th className="glass-text-secondary">User</th>
                  <th className="glass-text-secondary">Role</th>
                  <th className="glass-text-secondary">Managed By</th>
                  <th className="glass-text-secondary">Features</th>
                  <th className="glass-text-secondary">Status</th>
                  <th className="glass-text-secondary">Last Login</th>
                  <th className="glass-text-secondary">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users
                  .filter(u => {
                    const search = userSearch.toLowerCase();
                    return !search || 
                      u.email.toLowerCase().includes(search) ||
                      (u.display_name?.toLowerCase().includes(search)) ||
                      u.role.toLowerCase().includes(search);
                  })
                  .map((user) => (
                  <tr key={user.email} className={!user.active ? 'inactive-row' : ''}>
                    <td>
                      <div className="user-cell">
                        <strong className="glass-text-primary">
                          {user.display_name || user.email.split('@')[0]}
                        </strong>
                        <span className="user-email-small glass-text-muted">{user.email}</span>
                      </div>
                    </td>
                    <td>
                      {permissions?.can_edit_users && editingRole?.email === user.email ? (
                        <div className="role-edit-container">
                          <input
                            type="text"
                            value={editingRole.value}
                            onChange={(e) => setEditingRole({ ...editingRole, value: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') submitRoleChange();
                              if (e.key === 'Escape') cancelEditingRole();
                            }}
                            className={`role-input glass-input ${editingRole.value === 'admin' ? 'role-admin' : 'role-territory'}`}
                            title="Enter 'admin', a territory code, or a freeform flag-only role such as 'broker'"
                            autoFocus
                          />
                          <button
                            className="role-action-button role-confirm"
                            onClick={submitRoleChange}
                            title="Save"
                          >
                            ✓
                          </button>
                          <button
                            className="role-action-button role-cancel"
                            onClick={cancelEditingRole}
                            title="Cancel"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <div 
                          className={`role-display ${user.role === 'admin' ? 'role-admin' : 'role-territory'} ${!permissions?.can_edit_users ? 'read-only' : ''}`}
                          onClick={() => permissions?.can_edit_users && startEditingRole(user.email, user.role)}
                          title={permissions?.can_edit_users ? "Click to edit role" : user.role}
                        >
                          {user.role}
                        </div>
                      )}
                    </td>
                    <td>
                      {permissions?.can_edit_users && editingManagedBy?.email === user.email ? (
                        <div className="role-edit-container">
                          <input
                            type="text"
                            value={editingManagedBy.value}
                            onChange={(e) => setEditingManagedBy({ ...editingManagedBy, value: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') submitManagedByChange();
                              if (e.key === 'Escape') cancelEditingManagedBy();
                            }}
                            className="role-input glass-input managed-by-input"
                            placeholder="Enter manager name"
                            autoFocus
                          />
                          <button
                            className="role-action-button role-confirm"
                            onClick={submitManagedByChange}
                            title="Save"
                          >
                            ✓
                          </button>
                          <button
                            className="role-action-button role-cancel"
                            onClick={cancelEditingManagedBy}
                            title="Cancel"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <div 
                          className={`managed-by-display ${!permissions?.can_edit_users ? 'read-only' : ''}`}
                          onClick={() => permissions?.can_edit_users && startEditingManagedBy(user.email, user.managed_by || '')}
                          title={permissions?.can_edit_users ? "Click to edit managed by" : (user.managed_by || '—')}
                        >
                          {user.managed_by || <span className="glass-text-muted">—</span>}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="feature-flags-cell">
                        {FEATURE_DEFS.map((f) => {
                          const active = (user.feature_flags && user.feature_flags.length > 0
                            ? user.feature_flags
                            : DEFAULT_FEATURE_KEYS).includes(f.key);
                          return (
                            <button
                              key={f.key}
                              type="button"
                              className={`feature-chip ${active ? 'on' : 'off'} ${!permissions?.can_edit_users ? 'read-only' : ''}`}
                              onClick={() => permissions?.can_edit_users && handleToggleFeature(user, f.key)}
                              disabled={!permissions?.can_edit_users}
                              title={f.description}
                            >
                              {f.label}
                            </button>
                          );
                        })}
                      </div>
                    </td>
                    <td>
                      {permissions?.can_edit_users ? (
                        <button
                          className={`status-toggle ${user.active ? 'active' : 'inactive'}`}
                          onClick={() => handleToggleStatus(user)}
                        >
                          {user.active ? '✓ Active' : '✗ Inactive'}
                        </button>
                      ) : (
                        <span className={`status-display ${user.active ? 'active' : 'inactive'}`}>
                          {user.active ? '✓ Active' : '✗ Inactive'}
                        </span>
                      )}
                    </td>
                    <td className="last-login glass-text-secondary">
                      {user.last_login 
                        ? new Date(user.last_login).toLocaleDateString() 
                        : 'Never'}
                    </td>
                    <td>
                      {permissions?.can_reset_passwords && user.role !== 'admin' && (
                        <button
                          className="reset-password-button"
                          onClick={() => handleResetPassword(user.email)}
                          title="Reset password"
                        >
                          🔑
                        </button>
                      )}
                      {permissions?.is_admin && permissions?.can_reset_passwords && user.role === 'admin' && (
                        <button
                          className="reset-password-button"
                          onClick={() => handleResetPassword(user.email)}
                          title="Reset password"
                        >
                          🔑
                        </button>
                      )}
                      {permissions?.can_delete_users && (
                        <button
                          className="delete-button"
                          onClick={() => handleDeleteUser(user.email)}
                          title="Delete user"
                        >
                          🗑️
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
        )}

        {activeTab === 'broker' && canEditBrokerSettings && <BrokerSettingsSection />}
        {activeTab === 'data' && user?.is_admin && <CacheRefreshSection />}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
